import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  signInWithPopup, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  register: (username: string, email: string, password: string) => Promise<boolean>;
  loginWithGoogle: () => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Firebase認証状態の監視
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        const userData: User = {
          id: firebaseUser.uid,
          username: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
          email: firebaseUser.email || ''
        };
        setUser(userData);
      } else {
        // Firebase認証がない場合、ローカルストレージをチェック
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          setUser(JSON.parse(storedUser));
        } else {
          setUser(null);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // 従来のメール/パスワード認証（ローカルストレージ使用）
  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      // Firebaseでのメール/パスワード認証を試行
      try {
        const result = await signInWithEmailAndPassword(auth, username, password);
        const userData: User = {
          id: result.user.uid,
          username: result.user.displayName || username,
          email: result.user.email || username
        };
        setUser(userData);
        return true;
      } catch (firebaseError) {
        // Firebaseで失敗した場合、ローカル認証にフォールバック
        const users = JSON.parse(localStorage.getItem('users') || '[]');
        const foundUser = users.find((u: any) => u.username === username && u.password === password);
        
        if (foundUser) {
          const userWithoutPassword = { id: foundUser.id, username: foundUser.username, email: foundUser.email };
          setUser(userWithoutPassword);
          localStorage.setItem('user', JSON.stringify(userWithoutPassword));
          return true;
        }
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const register = async (username: string, email: string, password: string): Promise<boolean> => {
    try {
      // Firebaseでのユーザー作成を試行
      try {
        const result = await createUserWithEmailAndPassword(auth, email, password);
        const userData: User = {
          id: result.user.uid,
          username: username,
          email: email
        };
        setUser(userData);
        return true;
      } catch (firebaseError) {
        // Firebaseで失敗した場合、ローカル登録にフォールバック
        const users = JSON.parse(localStorage.getItem('users') || '[]');
        
        if (users.some((u: any) => u.username === username || u.email === email)) {
          return false;
        }

        const newUser = {
          id: Date.now().toString(),
          username,
          email,
          password
        };

        users.push(newUser);
        localStorage.setItem('users', JSON.stringify(users));

        const userWithoutPassword = { id: newUser.id, username: newUser.username, email: newUser.email };
        setUser(userWithoutPassword);
        localStorage.setItem('user', JSON.stringify(userWithoutPassword));
        return true;
      }
    } catch (error) {
      console.error('Registration error:', error);
      return false;
    }
  };

  // Googleログイン
  const loginWithGoogle = async (): Promise<boolean> => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const userData: User = {
        id: result.user.uid,
        username: result.user.displayName || 'Google User',
        email: result.user.email || ''
      };
      setUser(userData);
      return true;
    } catch (error) {
      console.error('Google login error:', error);
      return false;
    }
  };


  const logout = () => {
    signOut(auth);
    setUser(null);
    localStorage.removeItem('user');
  };

  const value = {
    user,
    login,
    register,
    loginWithGoogle,
    logout,
    isAuthenticated: !!user,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};